import { loadBalanceReduction } from '../api';
import React, { useState, useEffect } from 'react';
import BalanceReductionList from './BalanceReductionList';
import BalanceReductionForm from './BalanceReductionForm';
import BalanceReductionHistory from './BalanceReductionHistory';
import BalanceReductionQuickActionModal from './BalanceReductionQuickActionModal';

const BalanceReductionSection = ({ currentUser, permissions }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyRecordId, setHistoryRecordId] = useState(null);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [quickActionRecord, setQuickActionRecord] = useState(null);

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      
      // Pasar las células del usuario si requiere filtrado por células
      const userCells = currentUser && currentUser.role !== 'admin' && currentUser.cells ? currentUser.cells : null;
      const userRole = currentUser ? currentUser.role : null;
      const userEmail = currentUser ? currentUser.email : null;
      console.log('🔍 Balance ReductionSection - Cargando con células del usuario:', userCells);
      console.log('🔍 Balance ReductionSection - Rol del usuario:', userRole);
      console.log('🔍 Balance ReductionSection - Email del usuario:', userEmail);
      
      const recordsData = await loadBalanceReduction(userCells, userRole, userEmail);
      setRecords(recordsData || []);
    } catch (error) {
      console.error('Error loading balance reduction:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRecord = async (recordData) => {
    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      if (editingRecord) {
        // Actualizar registro existente
        const response = await fetch(`${API_URL}/balance-reduction-tracking/${editingRecord.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(recordData),
        });

        if (response.ok) {
          await loadRecords();
          setShowForm(false);
          setEditingRecord(null);
          alert('✅ Registro actualizado exitosamente');
        } else {
          throw new Error('Error al actualizar registro');
        }
      } else {
        // Crear nuevo registro
        const response = await fetch(`${API_URL}/balance-reduction-tracking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...recordData,
            user_name: currentUser?.email || 'Sistema'
          }),
        });

        if (response.ok) {
          await loadRecords();
          setShowForm(false);
          alert('✅ Registro creado exitosamente');
        } else {
          throw new Error('Error al crear registro');
        }
      }
    } catch (error) {
      console.error('Error al guardar registro:', error);
      alert('❌ Error al guardar el registro: ' + error.message);
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDeleteRecord = async (recordId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este registro?')) {
      try {
        // Determinar la URL de la API basada en el entorno
        const API_URL = process.env.NODE_ENV === 'production' 
          ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
          : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

        const response = await fetch(`${API_URL}/balance-reduction-tracking/${recordId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          await loadRecords();
          alert('✅ Registro eliminado exitosamente');
        } else {
          throw new Error('Error al eliminar registro');
        }
      } catch (error) {
        console.error('Error al eliminar registro:', error);
        alert('❌ Error al eliminar el registro: ' + error.message);
      }
    }
  };

  const handleViewHistory = (recordId) => {
    setHistoryRecordId(recordId);
    setShowHistory(true);
  };

  const handleQuickAction = (record) => {
    setQuickActionRecord(record);
    setShowQuickAction(true);
  };

  const handleSaveQuickAction = async (recordId, actionData) => {
    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/balance-reduction-tracking/${recordId}/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(actionData),
      });

      if (response.ok) {
        alert('✅ Acción registrada exitosamente');
        setShowQuickAction(false);
        setQuickActionRecord(null);
        
        // Si el historial está abierto para este registro, forzar actualización
        if (showHistory && historyRecordId === recordId) {
          // Cerrar y reabrir el historial para forzar recarga
          setShowHistory(false);
          setTimeout(() => {
            setHistoryRecordId(recordId);
            setShowHistory(true);
          }, 100);
        }
      } else {
        throw new Error('Error al registrar la acción');
      }
    } catch (error) {
      console.error('Error al guardar acción rápida:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        <span className="ml-3 text-lg text-gray-600">Cargando clientes que bajan saldo...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">📉 Clientes que Bajan Saldo</h1>
            <p className="text-red-100 mt-2">
              Seguimiento de clientes con reducción de presupuesto publicitario
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{records.length}</div>
            <div className="text-red-100">Registros Activos</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-4">
          <button
            onClick={() => {
              setEditingRecord(null);
              setShowForm(true);
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
          >
            <span className="mr-2">➕</span>
            Nuevo Registro
          </button>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Pendiente</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
            <span className="text-sm text-gray-600">En Proceso</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Completado</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Cancelado</span>
          </div>
        </div>
      </div>

      {/* Records List */}
      <BalanceReductionList
        records={records}
        onEdit={handleEditRecord}
        onDelete={handleDeleteRecord}
        onViewHistory={handleViewHistory}
        onQuickAction={handleQuickAction}
        currentUser={currentUser}
        permissions={permissions}
      />

      {/* Form Modal */}
      {showForm && (
        <BalanceReductionForm
          record={editingRecord}
          onSave={handleSaveRecord}
          onClose={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          currentUser={currentUser}
        />
      )}

      {/* History Modal */}
      {showHistory && historyRecordId && (
        <BalanceReductionHistory
          recordId={historyRecordId}
          onClose={() => {
            setShowHistory(false);
            setHistoryRecordId(null);
          }}
        />
      )}

      {/* Quick Action Modal */}
      {showQuickAction && quickActionRecord && (
        <BalanceReductionQuickActionModal
          record={quickActionRecord}
          onClose={() => {
            setShowQuickAction(false);
            setQuickActionRecord(null);
          }}
          onSave={handleSaveQuickAction}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default BalanceReductionSection;
